import {Button, Form, Input} from 'antd';
import {QRCodeCanvas} from 'qrcode.react';
import React, {useEffect, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';

import {DynamicIcon} from 'components/dynamic-icon';
import {ReactComponent as LockIcon} from 'shared/assets/auth/lock.svg';
import {ReactComponent as UserIcon} from 'shared/assets/auth/user.svg';
import {URL_LOGIN} from 'shared/constants/urls';
import {showMessage} from 'shared/utils';
import {useQueryParams} from 'shared/utils/query-params';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    AuthResponseTypes,
    authSliceActions,
    getTwoFACode,
    selectTwoFactorAuthTypeCode,
    ITwoFactorAuthStatus,
    selectAuthError,
    selectTwoFaCodeValidityTimeInSeconds,
    ETwoFactorAuthCode,
    check2faCode,
    EAuthRoutes,
} from 'store/slices/auth-slice';
import {selectIsThunkPending} from 'store/slices/loading-state-slice';

import {ETwoFactorAuthFormType} from './two-factor-auth-page-types';

import 'pages/two-factor-auth-page/two-factor-auth-form.less';

export const TwoFactorAuthPageForm = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const {getQueryParam} = useQueryParams();

    const twoFactorAuthTypeCode = useAppSelector(selectTwoFactorAuthTypeCode);
    const isLoading = useAppSelector(s => selectIsThunkPending(s, getTwoFACode.typePrefix));
    const [username, setUsername] = useState('');
    const [twoFactorCode, setTwoFactorCode] = useState('');
    const [currentForm, setCurrentForm] = useState<ETwoFactorAuthFormType>(ETwoFactorAuthFormType.CONNECT_FORM);
    const [twoFactorCodeExpirationDate, setTwoFactorCodeExpirationDate] = useState<Date | undefined>();
    const [status, setStatus] = useState<ITwoFactorAuthStatus | null>(null);
    const qrCodeValue = decodeURIComponent(getQueryParam('qr') || '');
    const isPortal2factorAuth = twoFactorAuthTypeCode === ETwoFactorAuthCode.PORTAL;

    const error = useAppSelector(selectAuthError);
    const twoFaCodeValidityTimeInSeconds = useAppSelector(selectTwoFaCodeValidityTimeInSeconds);

    useEffect(() => {
        if (qrCodeValue) setCurrentForm(ETwoFactorAuthFormType.QR_CODE_FORM);

        if (isPortal2factorAuth) {
            setCurrentForm(ETwoFactorAuthFormType.PORTAL_2FA_FORM);

            if (twoFaCodeValidityTimeInSeconds) {
                setTwoFactorCodeExpirationDate(new Date(Date.now() + twoFaCodeValidityTimeInSeconds * 1000));
            }
        }
    }, [isPortal2factorAuth, twoFaCodeValidityTimeInSeconds]);

    useEffect(() => () => {
        if (!isPortal2factorAuth) {
            dispatch(authSliceActions.resetError());
        }
    }, [isPortal2factorAuth]);

    const handleGetCodeBtnClick = () => {
        setStatus(null);
        dispatch(getTwoFACode({username}))
            .then(data => {
                setStatus(data?.payload ?? null);
                if (!data?.payload?.isError) {
                    setCurrentForm(ETwoFactorAuthFormType.MESSAGE_SENT_FORM);
                }
            });
    };

    const twoFactorPortalAuth = async () => {
        const currentDate = new Date();
        const isTokenExpired = currentDate > twoFactorCodeExpirationDate!

        if (isTokenExpired) {
            history.push('/login');
            showMessage({message: "Время действия кода истекло. Пожалуйста, войдите снова", isError: true})
            return;
        }

        const res = await dispatch(check2faCode({ twoFactorAuthCode: twoFactorCode }));

        if (typeof res?.payload === 'string') {
            return;
        }

        const isSuccess = res.payload?.code === 200
        const isPasswordExpired = res.payload?.code === 401 && res.payload?.type === AuthResponseTypes.PASSWORD_EXPIRED

        if (isPasswordExpired) {
            history.push(`${EAuthRoutes.CHANGE_PASSWORD}`);
            return;
        }

        if (isSuccess) {
            dispatch(authSliceActions.resetError());
            history.push('/');
        }
    };

    const getFormData = () => {
        let header;
        let fields;
        switch (currentForm) {
        case ETwoFactorAuthFormType.QR_CODE_FORM:
            header = (
                <>
                    <h1>Авторизация с 2fa</h1>
                    <div className="description qr-code-form__fields__description">
                        <h2>
                            Инструкция:
                        </h2>
                        <div className="description__composite">
                            <ol className="description__composite__list">
                                <li>Откройте приложение Google Authenticator</li>
                                <li>Нажмите + в правом нижнем углу</li>
                                <li>Выберите Сканировать QR-код</li>
                                <li>Наведите камеру на QR-код</li>
                            </ol>
                            <p>
                                В приложении добавится строка с наименованием TAX_MON: *почта,
                                Указанная в личном кабинете в Витрине*. Синим цветом
                                будет выделен 6-тизначный код, необходимый для входа.
                            </p>
                        </div>
                    </div>
                </>
            );
            fields = (
                <>
                    <Form.Item>
                        <Button
                            type="primary"
                            block
                            onClick={() => history.push(URL_LOGIN)}
                        >
                            Ввести данные
                        </Button>
                    </Form.Item>
                </>
            );
            return {
                header,
                fields,
            };
        case ETwoFactorAuthFormType.MESSAGE_SENT_FORM:
            header = (
                <>
                    <h1>
                        Подключение к 2fa
                    </h1>
                    <div className="description message-sent-form__header__description">
                        <p>
                            Сообщение было отправлено на email.<br />
                            Если ничего не пришло, попробуйте снова или обратитесь к администратору системы.
                        </p>
                    </div>
                </>
            );
            fields = (
                <>
                    <Form.Item>
                        <Link
                            to={URL_LOGIN}
                        >
                            <Button
                                type="primary"
                                block
                                loading={isLoading}
                            >
                                Авторизоваться с помощью 2fa
                            </Button>
                        </Link>
                    </Form.Item>
                    <Form.Item name="try-again-button">
                        <Button
                            className="try-again-button"
                            type="link"
                            block
                            onClick={() => setCurrentForm(ETwoFactorAuthFormType.CONNECT_FORM)}
                            loading={isLoading}
                        >
                            Попробовать еще раз
                        </Button>
                    </Form.Item>
                </>
            );
            return {
                header,
                fields,
            };
        case ETwoFactorAuthFormType.PORTAL_2FA_FORM:
            header = (
                <h1>
                    Авторизация
                </h1>
            );
            fields = (
                <>
                    <Form.Item
                        name="twoFactorAuthCode"
                    >
                        <Input
                            onChange={e => setTwoFactorCode(e.target.value)}
                            disabled={isLoading}
                            prefix={<LockIcon />}
                            type="password"
                            placeholder="Пароль 2fa"
                        />
                    </Form.Item>
                    {!!error?.message && (
                        <div className="login-form__error">
                            <DynamicIcon
                                type="ExclamationCircleOutlined"
                            />
                            {error.message}
                        </div>
                    )}
                    <div className="login-form__buttons-container">
                        <Button
                            htmlType="submit"
                            type="primary"
                            block
                            loading={isLoading}
                            onClick={twoFactorPortalAuth}
                        >
                            Войти
                        </Button>
                    </div>
                </>
            );
            return {
                header,
                fields,
            };
        default:
            header = (
                <>
                    <h1>
                        Подключение к 2fa
                    </h1>
                    <div
                        className="description connect-form__header__description"
                    >
                        <h2>
                            Примечание:
                        </h2>
                        <p>
                            Для двухфакторной аутентификации необходимо установить
                            мобильное приложение Google Authenticator на ваше устройство.
                            <br />
                            <br />
                            Пожалуйста, укажите имя пользователя и мы отправим ссылку
                            для подключения на email-почту
                        </p>
                    </div>
                </>
            );
            fields = (
                <>
                    {status && (
                        <div className="connect-form__fields__error">
                            <DynamicIcon
                                type="ExclamationCircleOutlined"
                            />
                            {status.message}
                        </div>
                    )}
                    <Form.Item
                        name="username"
                        rules={[{
                            required: true,
                            message: 'Введите имя пользователя',
                        }]}
                    >
                        <Input
                            disabled={isLoading}
                            onChange={e => setUsername(e.target.value)}
                            prefix={<UserIcon />}
                            placeholder="Пользователь"
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            block
                            onClick={handleGetCodeBtnClick}
                            loading={isLoading}
                        >
                            Получить ссылку
                        </Button>
                    </Form.Item>
                </>
            );
            return {
                header,
                fields,
            };
        }
    };

    const {header, fields} = getFormData();

    return (
        <Form
            className={`two-fa-form ${currentForm}`}
        >
            <div className={`two-fa-form__container ${currentForm}__container`}>
                <div className={`two-fa-form__container__header ${currentForm}__header`}>
                    {header}
                </div>
                <div className={`two-fa-form__container__fields ${currentForm}__fields`}>
                    {fields}
                </div>
                {qrCodeValue && currentForm === ETwoFactorAuthFormType.QR_CODE_FORM
                    && (
                        <QRCodeCanvas
                            className="two-fa-form__container__qr"
                            value={qrCodeValue || ''}
                            size={315}
                            bgColor="transparent"
                            fgColor="white"
                        />
                    )}
            </div>
        </Form>
    );
};
